import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Package, Truck, Factory } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const IMG_HERITAGE =
  "https://images.pexels.com/photos/5974046/pexels-photo-5974046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900";
const IMG_MODERN = "/brand/ikel-hinge-promo-full.jpg";

const SERVICE_ICONS = [Package, Factory, Truck];

export default function WhoWeAre() {
  const { t } = useLang();
  const w = t.common.whoWeAre;

  return (
    <section
      className="py-24 md:py-32 bg-white"
      id="about"
      data-testid="who-we-are-section"
    >
      <div className="pgb-container">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16">
          <div className="lg:col-span-5">
            <span className="eyebrow text-[#1D4ED8]">{w.eyebrow}</span>
            <h2 className="mt-4 text-3xl md:text-5xl lg:text-[58px] font-black tracking-tighter text-[#0B1B29] leading-[1]">
              {w.headline}
            </h2>
          </div>
          <div className="lg:col-span-7 text-[#536878] text-base md:text-lg leading-relaxed space-y-5">
            <p>{w.lede}</p>
            <p>{w.ikelLine}</p>
          </div>
        </div>

        {/* Then & Now visual */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-20" data-testid="then-and-now">
          <div
            className="relative group overflow-hidden bg-[#0B1B29]"
            data-testid="wwa-legacy-img"
          >
            <div className="aspect-[4/3]">
              <img
                src={IMG_HERITAGE}
                alt="Classic furniture workshop — 25 years of heritage"
                className="w-full h-full object-cover opacity-85 transition-transform duration-700 group-hover:scale-[1.04] saturate-[0.85]"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B29] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-[11px] font-mono tracking-[0.28em] uppercase text-[#F59E0B] mb-2">
                {w.legacyKicker}
              </div>
              <div className="text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: "Chivo" }}>
                {w.legacyHeadline}
              </div>
            </div>
          </div>
          <div
            className="relative group overflow-hidden bg-[#0B1B29]"
            data-testid="wwa-modern-img"
          >
            <div className="aspect-[4/3]">
              <img
                src={IMG_MODERN}
                alt="Modern İKEL MT precision hardware"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B29] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-[11px] font-mono tracking-[0.28em] uppercase text-[#93C5FD] mb-2">
                {w.modernKicker}
              </div>
              <div className="text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: "Chivo" }}>
                {w.modernHeadline}
              </div>
            </div>
          </div>
        </div>

        {/* Triple-A Service */}
        <div className="mb-20">
          <div className="max-w-2xl mb-10">
            <span className="eyebrow">{w.servicesEyebrow}</span>
            <h3 className="mt-3 text-2xl md:text-4xl font-black tracking-tight text-[#0B1B29]">
              {w.servicesHeadline}
            </h3>
            <p className="mt-4 text-[#536878] leading-relaxed">
              {w.servicesLede}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {w.services.map((s, idx) => {
              const Icon = SERVICE_ICONS[idx];
              return (
                <div
                  key={s.title}
                  className="bg-white p-8 md:p-10"
                  data-testid={`wwa-service-${idx}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-mono text-xs tracking-[0.22em] uppercase text-[#1D4ED8]">
                      0{idx + 1} / {s.tag}
                    </span>
                  </div>
                  <Icon size={28} className="text-[#0B1B29] mb-5" />
                  <h4 className="text-xl font-bold text-[#0B1B29] tracking-tight">{s.title}</h4>
                  <p className="mt-3 text-sm text-[#536878] leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-5">
            <span className="eyebrow text-[#F59E0B]">{w.visionEyebrow}</span>
            <h3 className="mt-3 text-2xl md:text-4xl font-black tracking-tight text-[#0B1B29] leading-[1.1]">
              {w.visionHeadline}
            </h3>
            <p className="mt-5 text-[#536878] leading-relaxed">{w.visionLede}</p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {w.audiences.map((a, idx) => (
              <div
                key={a.title}
                className="bg-[#0B1B29] text-white p-7 md:p-8"
                data-testid={`wwa-audience-${idx}`}
              >
                <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-[#93C5FD] mb-3">
                  {a.tag}
                </div>
                <h4 className="text-xl md:text-2xl font-black tracking-tight">{a.title}</h4>
                <p className="mt-3 text-sm text-white/75 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Closing philosophy + CTA */}
        <div
          className="relative bg-[#0B1B29] text-white p-10 md:p-16 overflow-hidden"
          data-testid="wwa-closing"
        >
          <div
            aria-hidden="true"
            className="absolute -top-24 -right-24 w-[420px] h-[420px] bg-[#1D4ED8] blur-[140px] opacity-40"
          />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#F59E0B] font-bold mb-4">
                <ShieldCheck size={14} />
                <span>{w.philosophyTag}</span>
              </div>
              <h3 className="text-3xl md:text-5xl font-black tracking-tighter leading-[1.05]">
                {w.philosophyTitle}
              </h3>
              <p className="mt-5 text-white/80 leading-relaxed max-w-2xl">
                {w.philosophyBody}
              </p>
              <p className="mt-6 text-[#93C5FD] font-bold tracking-tight text-lg md:text-xl">
                {w.closingTagline}
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Link
                to="/quote"
                className="btn-accent"
                data-testid="wwa-cta-quote"
              >
                {t.common.requestQuote} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
